import * as React from "react";
import {
  failure,
  fold,
  getOrElse,
  initial,
  pending,
  RemoteData,
  success,
} from "@devexperts/remote-data-ts";
import { Box, CircularProgress } from "@material-ui/core";
import { includes, take } from "lodash";
import { Link, withRouter } from "react-router-dom";

import { Image } from "components";
import Button from "components/Button";
import {
  AbstractRootComponent,
  RootComponentProps,
} from "containers/Responsive/AbstractRootComponent";
import { BackButtonLogoHeader } from "containers/Responsive/BackButtonLogoHeader";
import { Header3 } from "containers/Responsive/Header3";
import { Listing } from "containers/Responsive/Listing";
import { Optional } from "containers/Responsive/Optional";
import { AppContext } from "contexts";
import {
  capitalize,
  filterWarnings,
  formatMoney,
  goToVP,
  mountQueryString,
  singularOrPlural,
  valueIsDefined,
} from "lib/helpers";
import * as routes from "routes";
import { getCurrentUser } from "services/users";
import { getValuationResult } from "services/valuation";
import {
  Source,
  Valuation,
  ValuationResult,
  ValuationResultKind,
} from "services/valuation/types";

import {
  ABOUT_ROYALTY_EXCHANGE_URL,
  PRIVACY_POLICY_URL,
  TERMS_AND_CONDITIONS_URL,
} from "../../constants";
import { BarChart } from "./BarChart";
import { NavigableSections } from "./NavigableSections";
import { ResponsiveModal } from "./ResponsiveModal";
import { RevenueChart } from "./RevenueChart";
import { ShareModal } from "./ShareModal";
import { ShareWithAFriend } from "./ShareWithAFriend";
import { SourceBreakdown } from "./SourceBreakdown";
import { OptionalValuationMetric } from "./ValuationMetric";
import {
  getAvgSongEarning,
  getEarningsSince,
  getMostInSingleYear,
  getRegionsRate,
  getReportOnTopSources,
  getTopSongShare,
  parseChartDataIntoTuples,
} from "./valuationReportPostProcessor";
import { ValuationSection } from "./ValuationSection";

const royaltyPayorsWithQuarterlyDistributions = ["BMI", "ASCAP"];

const hasQuarterlyDistribution = (royaltyPayor: string): boolean =>
  includes(royaltyPayorsWithQuarterlyDistributions, royaltyPayor);

const getDisplayResult = (valuation: Valuation): ValuationResult | null => {
  const fullCatalogResult = valuation.results.find(
    r => r.kind === ValuationResultKind.FULL_CATALOG,
  );
  if (fullCatalogResult) {
    return fullCatalogResult;
  }

  if (valuation.chosen_result) {
    return (
      valuation.results.find(r => r.id === valuation.chosen_result) ?? null
    );
  }

  if (valuation.version === 1) {
    return (
      valuation.results.find(
        r => r.kind === ValuationResultKind.SCRAPED_TRACK_LIST,
      ) ?? null
    );
  }

  return null;
};

const royaltyPayorsWithCountriesMapped = [
  "AdminMP",
  "ASCAP",
  "BMI",
  "DistroKid",
  "SESAC",
  "Sony Music Publishing",
  "TuneCore",
  "Vydia",
];
const hasCountriesMapped = (royaltyPayor: string): boolean =>
  includes(royaltyPayorsWithCountriesMapped, royaltyPayor);

const formatCountryName = (royaltyPayor: string, countryName: string) =>
  hasCountriesMapped(royaltyPayor)
    ? countryName
    : countryName === "United States"
    ? "Domestic"
    : "International";

interface State {
  valuation: RemoteData<string, Valuation>;
  userId: RemoteData<string, number>;
  showModal: boolean;
}

export class ValuationResults extends AbstractRootComponent<State> {
  static contextType = AppContext;
  context!: React.ContextType<typeof AppContext>;
  _isMounted = false;
  protected valuationId: number;

  public constructor(props: RootComponentProps) {
    super(props);
    this.className = "valuation-results-root";
    this.renderOn3Views = true;

    this.state = {
      valuation: initial,
      userId: initial,
      showModal: false,
    };

    this.valuationId = parseInt(this.props.match.params.valuationId, 10);
  }

  public body() {
    const { valuation, userId, showModal } = this.state;

    return fold<string, Valuation, React.ReactElement>(
      () => <RemoteDataLoading />,
      () => <RemoteDataLoading />,
      err => <RemoteDataError reason={err} />,
      valuationReport => {
        return (
          <ValuationResultsBody
            userId={getOrElse(() => -1)(userId)}
            valuationId={this.valuationId}
            valuationReport={valuationReport}
            showModal={showModal}
            closeModal={() => this.setState({ showModal: false })}
            history={this.props.history}
          />
        );
      },
    )(valuation);
  }

  public header() {
    return (
      <div id="valuation-results-header-container">
        <BackButtonLogoHeader
          backText="Your Account"
          onClick={() => {
            const currentUrl = new URL(window.location.toLocaleString());
            if (currentUrl.pathname.includes("/preview")) {
              const valuationId = window.location.pathname.split("/")[4];
              return goToVP(
                undefined,
                false,
                `valuation-analysis/${valuationId}`,
              );
            }
            return this.props.history.push(routes.sellerDashboard());
          }}
          right={
            <Button
              variant="contained"
              customColor="green"
              onClick={() => this.setState({ showModal: true })}
              style={{ whiteSpace: "nowrap" }}
            >
              Invite a Friend
            </Button>
          }
        />
      </div>
    );
  }

  public footer() {
    return (
      <div id="valuation-results-footer-container">
        <Header3
          left={
            <>
              <Link target="_blank" to={routes.sellerDashboard()}>
                <Image
                  alt="Royalty Exchange - Know Your Worth (beta)"
                  name="orderbook/kyw_logo.svg"
                />
              </Link>
            </>
          }
          right={
            <>
              <div className="valuation-results-footer-right">
                <label>© Royalty Exchange 2019</label>
                <a
                  href={ABOUT_ROYALTY_EXCHANGE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  About
                </a>
                <a
                  href={TERMS_AND_CONDITIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Use
                </a>
                <a
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </div>
            </>
          }
          className="valuation-results-footer"
        />
      </div>
    );
  }

  public componentDidMount() {
    this._isMounted = true;
    this.setState(
      {
        valuation: pending,
        userId: pending,
      },
      async () => {
        try {
          const user = await getCurrentUser();
          if (this._isMounted) {
            this.setState({
              userId: success(user.userId),
            });
          }
          try {
            const valuationResult = await getValuationResult(this.valuationId);
            switch (valuationResult.state) {
              case "failed":
                this.props.history.push(
                  routes.sellerValuationLoading() +
                    mountQueryString({
                      asset_id: valuationResult.asset,
                      showError: valuationResult.status_message,
                      royaltyPayor: valuationResult.royalty_payor,
                    }),
                );
                break;
              case "pending":
              case "waiting":
                this.props.history.push(
                  routes.sellerValuationLoading() +
                    mountQueryString({
                      asset_id: valuationResult.asset,
                    }),
                );
                break;
              case "completed":
                if (!valueIsDefined(valuationResult.results)) {
                  return Promise.reject(valuationResult.state);
                }
                if (this._isMounted) {
                  this.setState({
                    valuation: success(valuationResult),
                  });
                }
                break;
            }
          } catch (error: any) {
            this.props.history.push(
              routes.sellerValuationLoading() +
                mountQueryString({
                  showError: error.statusMessage,
                  royaltyPayor: error.royaltyPayor,
                }),
            );
            if (this._isMounted) {
              this.setState({
                valuation: failure("error:" + JSON.stringify(error)),
              });
            }
          }
        } catch (error) {}
      },
    );
  }

  public componentWillUnmount() {
    this._isMounted = false;
    const { notificationMessages, setNotificationMessages } = this.context;
    setNotificationMessages(filterWarnings(notificationMessages));
  }
}

interface Props {
  valuationReport: Valuation;
  userId: number;
  valuationId: number;
  showModal: boolean;
  closeModal: () => void;
  history: History;
}

class ValuationResultsBody extends React.Component<Props> {
  protected ref: React.RefObject<HTMLDivElement>[];

  public constructor(props: Props) {
    super(props);
    this.ref = [
      React.createRef<HTMLDivElement>(),
      React.createRef<HTMLDivElement>(),
      React.createRef<HTMLDivElement>(),
      React.createRef<HTMLDivElement>(),
      React.createRef<HTMLDivElement>(),
    ];
  }

  public render() {
    const {
      valuationReport,
      userId,
      showModal,
      valuationId,
      closeModal,
    } = this.props;

    const royaltyPayorDisplayName = valuationReport.royalty_payor;
    const report = getDisplayResult(valuationReport);
    if (!report) {
      return null;
    }
    const statementsCount = report.statement_count || 0;
    const mostInSingleYear = getMostInSingleYear(report);
    const topSources = getReportOnTopSources(report.top_sources);
    const regionsRate = getRegionsRate(report);
    const nonDomesticPercentage = Math.round(
      ((report.regions.intl.lte + report.regions.unreported.lte) / report.lte) *
        100,
    );

    return (
      <>
        <div id="valuation-results-container">
          <ResponsiveModal
            id="valuation-results-modal"
            active={showModal}
            closeModal={() => closeModal()}
          >
            <ShareModal valuationId={valuationId} userId={userId} />
          </ResponsiveModal>

          <NavigableSections
            refs={this.ref}
            dataVersion={valuationReport.version}
          >
            <ValuationSection
              minimumVersion={1}
              title={`Your ${royaltyPayorDisplayName} Catalog Analysis`}
              subtitle={
                `Know Your Worth analyzed ${statementsCount}` +
                ` ${singularOrPlural(
                  "statement",
                  "statements",
                  statementsCount,
                )} for this report`
              }
              menuTitle="Topline Stats"
              className="topline-stats"
              externalRef={this.ref[0]}
            >
              <Listing key="top-line-listing" usesSeparator>
                {royaltyPayorDisplayName !== "ASCAP" && (
                  <OptionalValuationMetric
                    dependencies={[report.most_recent_payment]}
                    prefix="$"
                    value={() => formatMoney(report.most_recent_payment, 0)}
                    text="Most Recent Payment"
                  />
                )}
                <OptionalValuationMetric
                  dependencies={[report.earnings_by_region]}
                  value={() => getEarningsSince(report)}
                  text="Earning Since"
                />
                <OptionalValuationMetric
                  dependencies={[report.lte]}
                  prefix="$"
                  value={() => formatMoney(report.lte, 0)}
                  text="Total Earnings"
                />
              </Listing>
            </ValuationSection>
            <ValuationSection
              minimumVersion={1}
              title="Overall Catalog"
              subtitle="What's your music been up to?"
              menuTitle="Overall Catalog"
              className="overall-catalog"
              externalRef={this.ref[1]}
            >
              <Listing key="overall-catalog-listing" usesSeparator>
                <OptionalValuationMetric
                  dependencies={[report.ltm]}
                  prefix="$"
                  value={() => formatMoney(Number(report.ltm) / 365, 2)}
                  text="was earned by your music every single day last year"
                />
                <OptionalValuationMetric
                  dependencies={[report.track_count]}
                  value={() => report.track_count}
                  text={`${singularOrPlural(
                    "song is",
                    "songs are",
                    report.track_count,
                  )} in your catalog`}
                />
                <OptionalValuationMetric
                  dependencies={[report.earnings_by_region]}
                  prefix="$"
                  value={() => formatMoney(mostInSingleYear.totalAmount, 0)}
                  text={() =>
                    `is the most your catalog earned in a single year (${mostInSingleYear.year})`
                  }
                />
              </Listing>
              <Optional dependencies={[report.earnings_by_region]}>
                <RevenueChart
                  data={parseChartDataIntoTuples(report.earnings_by_region)}
                  distributionFrequency={valuationReport.distribution_frequency}
                />
              </Optional>
            </ValuationSection>
            <ValuationSection
              minimumVersion={1}
              title="Song-by-Song"
              subtitle="Which songs are driving your catalog?"
              menuTitle="Song-by-Song"
              className="song-by-song"
              externalRef={this.ref[2]}
            >
              <Listing key="song-by-song-listing" usesSeparator>
                <OptionalValuationMetric
                  dependencies={[report.top_tracks, report.lte]}
                  value={() => getTopSongShare(report)}
                  suffix="%"
                  text={`of your earnings come from "${capitalize(
                    report.top_tracks[0].name.toLocaleLowerCase(),
                  )}"`}
                />
                <OptionalValuationMetric
                  dependencies={[report.tracks_with_earnings_count]}
                  value={() => report.tracks_with_earnings_count}
                  text={`${singularOrPlural(
                    "song",
                    "songs",
                    report.tracks_with_earnings_count || 2,
                  )} in your catalog have earned royalties`}
                />
                <OptionalValuationMetric
                  dependencies={[report.lte, report.tracks_with_earnings_count]}
                  prefix="$"
                  value={() => getAvgSongEarning(report)}
                  text="is the average earnings for every song in your catalog"
                />
              </Listing>
              <Optional dependencies={[report.top_tracks]}>
                <BarChart
                  data={report.top_tracks}
                  totalAmount={report.lte}
                  headerText={() => {
                    const numTracks = report.top_tracks.length;
                    return (
                      `Top ${numTracks} ` +
                      `Earning ${singularOrPlural("Song", "Songs", numTracks)}`
                    );
                  }}
                  columnTitle={(count: number) =>
                    `Top ${singularOrPlural("Song", "Songs", count)}`
                  }
                />
              </Optional>
            </ValuationSection>
            <ValuationSection
              minimumVersion={1}
              title="Sources and Formats"
              subtitle="Are you a streaming superstar, or are you all about the radio play?"
              menuTitle="Sources and Formats"
              className="sources-and-formats"
              externalRef={this.ref[3]}
            >
              <Listing key="sources-and-formats-listing" usesSeparator>
                {take(topSources, 3).map((s: Source) => (
                  <OptionalValuationMetric
                    key={`metric-${s.name}`}
                    dependencies={[report.top_sources, s.track_count]}
                    value={() => s.track_count}
                    text={`${singularOrPlural(
                      "song",
                      "songs",
                      s.track_count,
                    )} earn ${s.name} Royalties`}
                  />
                ))}
              </Listing>
              <Optional dependencies={[report.lte, report.top_sources]}>
                <SourceBreakdown
                  data={topSources}
                  totalValue={report.lte}
                  title="Source Breakdown"
                />
              </Optional>
            </ValuationSection>
            <ValuationSection
              minimumVersion={5}
              title="Geography"
              subtitle="Where's your music earning?"
              menuTitle="Geography"
              className="geography"
              externalRef={this.ref[4]}
            >
              <Listing key="geography-listing" usesSeparator>
                <OptionalValuationMetric
                  dependencies={[report.regions, regionsRate]}
                  value={() => regionsRate.value}
                  text={() => regionsRate.label}
                  suffix="x"
                />
                <OptionalValuationMetric
                  dependencies={[report.regions, report.lte]}
                  value={() => nonDomesticPercentage}
                  suffix="%"
                  text="of your royalties are paid from outside of the US"
                />
                <OptionalValuationMetric
                  dependencies={[
                    report.country_count,
                    royaltyPayorDisplayName === "Sony Music Publishing"
                      ? undefined
                      : true,
                  ]}
                  value={() => report.country_count}
                  text={`${singularOrPlural(
                    "country has",
                    "different countries have",
                    report.country_count,
                  )} paid you royalties`}
                />
              </Listing>
              <Optional dependencies={[report.top_countries]}>
                <BarChart
                  data={
                    report.top_countries &&
                    report.top_countries.map(c => ({
                      ...c,
                      name: formatCountryName(royaltyPayorDisplayName, c.name),
                    }))
                  }
                  totalAmount={report.lte}
                  headerText="Geography Breakdown"
                  columnTitle={(count: number) =>
                    hasQuarterlyDistribution(royaltyPayorDisplayName)
                      ? `Top ${singularOrPlural("Country", "Countries", count)}`
                      : "Location"
                  }
                />
              </Optional>
            </ValuationSection>
          </NavigableSections>
          <ShareWithAFriend valuationId={valuationId} userId={userId} />
        </div>
      </>
    );
  }
}

function RemoteDataLoading() {
  return (
    <Box
      display="flex"
      justifyContent="center"
      height="100vh"
      paddingTop="100px"
    >
      <CircularProgress size={50} />
    </Box>
  );
}

function RemoteDataError(props: { reason?: string }) {
  return <>Error: {props.reason}</>;
}

const ValuationResultsWithRouter = withRouter(ValuationResults);

export default ValuationResultsWithRouter;
